<template>
  <messenger-view-layout
    :title="headerTitle"
    :subtitle="headerSubtitle"
    :is-loading="isChatLoadingState(chatState)"
    class="messenger-chat-view-layout"
    @back="$emit('back')"
  >
    <template v-slot:channel-icon>
      <widget-skeleton
        v-if="isChatLoadingState(chatState)"
        dark
        type="circle"
        width="40px"
        height="40px"
      />
      <div
        v-else
        v-bind="getThemeIconProps('chat-header-avatar')"
        class="chat-header-avatar"
        :class="{
          'chat-header-avatar--online': !!headerAvatarUrl,
        }"
        :style="{
          backgroundColor: backgroundContrastColor,
          borderColor: backgroundContrastColor,
        }"
      >
        <img
          v-if="headerAvatarUrl"
          :src="headerAvatarUrl"
        />
        <widget-icon
          v-else
          name="tmi-message-bubble"
          size="20px"
        />
      </div>
    </template>

    <template
      v-if="!isChatEndedState(chatState)"
      v-slot:header-right
    >
      <widget-dropdown position="bottom-left">
        <widget-button
          v-bind="themeTransparentButtonOnBackgroundProps"
          is-icon-only
          size="28"
          view="transparent"
        >
          <widget-icon
            name="more_vert"
            size="24px"
          />
        </widget-button>

        <template v-slot:menu>
          <widget-dropdown-item
            class="error--text"
            @click="$emit('end-chat')"
          >
            <widget-icon
              name="tmi-logout-exit"
              size="16px"
              class="mr-2"
            />
            <span>{{ $t('chat.endChat') }}</span>
          </widget-dropdown-item>
        </template>
      </widget-dropdown>
    </template>

    <template v-slot:alert>
      <slot name="alert" />
    </template>

    <template v-slot>
      <slot />
    </template>
  </messenger-view-layout>
</template>

<script setup lang="ts">
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import MessengerViewLayout from '@widgets/messenger/components/MessengerViewLayout.vue'
import WidgetDropdown from '@widgets/shared/components/dropdown/WidgetDropdown.vue'
import WidgetDropdownItem from '@widgets/shared/components/dropdown/WidgetDropdownItem.vue'
import {
  isChatEndedState,
  isChatLoadingState,
  isChatOpenedState,
  type MessengerChatState,
} from '@widgets/messenger/types/chat/chat'
import translateService from '@widgets/shared/services/translate/translateService'
import WidgetSkeleton from '@widgets/shared/components/WidgetSkeleton.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { computed } from '@/composition/vue/compositionApi'

const props = defineProps<{
  chatState: MessengerChatState
  agentTypingCount: number
}>()

defineEmits<{
  (e: 'back'): void
  (e: 'end-chat'): void
}>()

const { getThemeIconProps, backgroundContrastColor, themeTransparentButtonOnBackgroundProps } = useWidgetTheme()

const headerTitle = computed(() => {
  return isChatOpenedState(props.chatState) ? props.chatState.agent.name : translateService.t('chat.liveChat')
})

const headerSubtitle = computed(() => {
  if (isChatOpenedState(props.chatState)) {
    if (props.agentTypingCount > 0) {
      return translateService.t('chat.typing')
    }
    return translateService.t('chat.supportAgent')
  }
  if (isChatEndedState(props.chatState)) {
    if (props.chatState.isOffline) {
      return ''
    }

    return props.chatState.isNoAgents
      ? translateService.t('chat.noAgentsAvailable')
      : translateService.t('chat.chatHasEnded')
  }
  return ''
})

const headerAvatarUrl = computed(() => {
  if (isChatOpenedState(props.chatState)) {
    return props.chatState.agent.avatarUrl
  }
  return ''
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.messenger-chat-view-layout {
  max-height: 100%;
}

.chat-header-avatar {
  @include widget-color-variants();
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 2px solid $white;
  background-color: $white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 999px;
  }

  &--online {
    &::after {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      border-radius: 999px;
      border: 2px solid $white;
      background-color: $green;
      bottom: -4px;
      right: -4px;
    }
  }
}
</style>
